import { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/ui/page-header';
import { useAuth } from '../context/AuthContext';
import { HelpButton } from '../components/ui/help-button';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { RightPanel } from '../components/ui/right-panel';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Building2, 
  Users, 
  Mail, 
  Phone, 
  MapPin,
  Globe,
  Edit,
  Save,
  Loader2,
  Shield,
  Calendar,
  Upload,
  Image as ImageIcon,
  Info,
  AlertTriangle,
  UserPlus,
  Trash2,
  Clock,
  X,
  Eye,
  ClipboardList,
  Package
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import AuditLogPanel from '../components/AuditLogPanel';

import { SectionTabs, SECTIONS } from '../components/enterprise';

const OrganizationPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [organization, setOrganization] = useState(null);
  const fileInputRef = useRef(null);
  const [originalData, setOriginalData] = useState(null);

  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviting, setInviting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Nederland',
    phone: '',
    website: '',
    kvk_number: '',
    btw_number: '',
    logo_url: '',
    sector: ''
  });

  useEffect(() => {
    fetchOrganization();
    fetchTeam();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await api.get('/user/organization');
      setOrganization(response.data);
      const data = {
        name: response.data.name || '',
        address: response.data.address || '',
        city: response.data.city || '',
        postal_code: response.data.postal_code || '',
        country: response.data.country || 'Nederland',
        phone: response.data.phone || '',
        website: response.data.website || '',
        kvk_number: response.data.kvk_number || '',
        btw_number: response.data.btw_number || '',
        logo_url: response.data.logo_url || '',
        sector: response.data.sector || ''
      };
      setFormData(data);
      setOriginalData(data);
    } catch (error) {
      console.error('Error fetching organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    setLoadingTeam(true);
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get('/user/organization/members'),
        api.get('/user/organization/invites')
      ]);
      setTeamMembers(membersRes.data.members || []);
      setPendingInvites(invitesRes.data.invites || []);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Vul een e-mailadres in');
      return;
    }
    setInviting(true);
    try {
      await api.post('/user/organization/invite', { email: inviteEmail, role: inviteRole });
      toast.success(`Uitnodiging verzonden naar ${inviteEmail}`);
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole('user');
      fetchTeam();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De uitnodiging kon niet worden verstuurd. Controleer het e-mailadres.'));
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await api.patch(`/user/organization/members/${memberId}/role?role=${newRole}`);
      toast.success('Rol gewijzigd');
      fetchTeam();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De rol kon niet worden gewijzigd. Probeer het later opnieuw.'));
    }
  };

  const handleRemoveMember = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await api.delete(`/user/organization/members/${confirmRemove.id}`);
      toast.success(`${confirmRemove.full_name || confirmRemove.email} verwijderd`);
      setConfirmRemove(null);
      fetchTeam();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het teamlid kon niet worden verwijderd. Probeer het later opnieuw.'));
    } finally {
      setRemoving(false);
    }
  };

  const handleCancelInvite = async (token) => {
    try {
      await api.delete(`/user/organization/invites/${token}`);
      toast.success('Uitnodiging geannuleerd');
      fetchTeam();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De uitnodiging kon niet worden geannuleerd. Probeer het later opnieuw.'));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Alleen afbeeldingen zijn toegestaan');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Bestand is te groot (max 2MB)');
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await api.post('/user/organization/logo', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, logo_url: response.data.logo_url }));
      setOrganization(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo geüpload');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Het logo kon niet worden geüpload. Controleer het bestandsformaat.'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/user/organization', formData);
      toast.success('Organisatie bijgewerkt');
      setEditing(false);
      fetchOrganization();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De organisatiegegevens konden niet worden opgeslagen. Probeer het later opnieuw.'));
    } finally {
      setSaving(false);
    }
  };

  // Check for unsaved changes
  const hasChanges = editing && originalData && JSON.stringify(formData) !== JSON.stringify(originalData);

  // Warn on navigation with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </MainLayout>
    );
  }

  // Check if profile is incomplete
  const requiredFields = ['name', 'address', 'postal_code', 'city', 'phone'];
  const missingFields = requiredFields.filter(field => !formData[field]);
  const isProfileIncomplete = missingFields.length > 0;

  // Field tooltip descriptions
  const fieldTooltips = {
    kvk: 'Uw Kamer van Koophandel-nummer. Dit wordt gebruikt voor de verificatie van uw bedrijf en verschijnt op facturen.',
    btw: 'Uw btw-identificatienummer (bijv. NL123456789B01). Nodig voor correcte facturatie binnen de EU.',
    sector: 'Uw bedrijfssector helpt ons om branche-specifieke beveiligingsaanbevelingen en benchmarks te bieden.'
  };

  return (
    <MainLayout>
      <SectionTabs tabs={SECTIONS.organisatie.tabs} />
      <div className="p-6 lg:p-8 space-y-8">
        {/* Profile Incomplete Banner */}
        {isProfileIncomplete && !editing && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-amber-800">Profiel incompleet</h3>
              <p className="text-sm text-amber-700 mt-1">
                Vul de ontbrekende gegevens in zodat we u beter kunnen helpen. 
                Ontbrekend: <strong>{missingFields.map(f => {
                  const labels = { name: 'Naam', address: 'Adres', postal_code: 'Postcode', city: 'Plaats', phone: 'Telefoon' };
                  return labels[f] || f;
                }).join(', ')}</strong>
              </p>
              <Button 
                size="sm" 
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => setEditing(true)}
              >
                <Edit className="mr-2 h-4 w-4" />
                Nu invullen
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <PageHeader
          title="Organisatie"
          tooltip="Beheer uw organisatie-instellingen, logo, branche en framework-keuze. Nodig teamleden uit en wijs rollen toe."
          testId="organization-title"
        >
          <div className="flex items-center gap-3">
            <HelpButton steps={[
              { title: 'Bedrijfsgegevens', description: 'Upload uw logo en vul de basisgegevens in: naam, KVK, BTW en adresgegevens.', target: null },
              { title: 'Branche & Framework', description: 'Kies uw sector en het beveiligingsframework dat past bij uw organisatie (ISO 27001, NIS2, etc.).', target: '[data-testid="org-sector-select"]' },
              { title: 'Team', description: 'Nodig teamleden uit en wijs rollen toe. Elk lid krijgt automatisch een eigen account.', target: '[data-testid="team-card"]', tip: 'U kunt rollen toewijzen zodat collega\'s alleen relevante secties zien.' },
            ]} />
            {!editing ? (
            <></>
          ) : (
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-xs" data-testid="unsaved-changes-badge">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Niet-opgeslagen wijzigingen
                </Badge>
              )}
              <Button 
                variant="outline"
                onClick={() => { setEditing(false); setEditSection(null); setFormData(originalData); }}
              >
                Annuleren
              </Button>
              <Button 
                className="bg-black text-white hover:bg-neutral-800"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-organization-button"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" strokeWidth={1.5} />}
                Opslaan
              </Button>
            </div>
          )}
          </div>
        </PageHeader>

        {/* Samengevoegd */}
        <div className="space-y-6">
          {/* Bedrijfsgegevens */}

        <div className="grid gap-6 lg:grid-cols-4">
          {/* Logo Card */}
          <Card className="lg:col-span-1 border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ImageIcon className="h-5 w-5" strokeWidth={1.5} />
                Bedrijfslogo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center gap-6">
                <div className="h-28 w-28 rounded-xl border-2 border-dashed border-neutral-200 flex items-center justify-center bg-neutral-50 overflow-hidden">
                  {formData.logo_url ? (
                    <img 
                      src={formData.logo_url} 
                      alt="Bedrijfslogo" 
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-10 w-10 text-neutral-300" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  data-testid="upload-logo-button"
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Logo uploaden
                </Button>
                <p className="text-xs text-neutral-400 text-center">PNG, JPG of SVG. Max 2MB.</p>
              </div>
            </CardContent>
          </Card>

          {/* Organization Info - Grid Layout */}
          <Card className="lg:col-span-3 border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="h-5 w-5" strokeWidth={1.5} />
                Bedrijfsgegevens
              </CardTitle>
              {!editing && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setEditing(true); setEditSection('info'); }}
                  data-testid="edit-info-button"
                >
                  <Edit className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                  Bewerken
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Bedrijfsnaam</Label>
                  {editing ? (
                    <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="org-name-input" />
                  ) : (
                    <p className="text-sm text-black font-medium">{organization?.name || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="kvk" className="text-xs font-medium text-neutral-500 uppercase tracking-wider inline-flex items-center gap-1 cursor-help">
                          KVK Nummer <Info className="h-3 w-3 text-neutral-400" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs">
                        {fieldTooltips.kvk}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {editing ? (
                    <Input id="kvk" value={formData.kvk_number} onChange={(e) => setFormData({ ...formData, kvk_number: e.target.value })} placeholder="12345678" data-testid="org-kvk-input" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.kvk_number || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="btw" className="text-xs font-medium text-neutral-500 uppercase tracking-wider inline-flex items-center gap-1 cursor-help">
                          BTW Nummer <Info className="h-3 w-3 text-neutral-400" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px] text-xs">
                        {fieldTooltips.btw}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {editing ? (
                    <Input id="btw" value={formData.btw_number} onChange={(e) => setFormData({ ...formData, btw_number: e.target.value })} placeholder="NL123456789B01" data-testid="org-btw-input" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.btw_number || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="address" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Adres</Label>
                  {editing ? (
                    <Input id="address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Straatnaam 123" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.address || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="postal" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Postcode</Label>
                  {editing ? (
                    <Input id="postal" value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="1234 AB" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.postal_code || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="city" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Plaats</Label>
                  {editing ? (
                    <Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Amsterdam" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.city || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="country" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Land</Label>
                  {editing ? (
                    <Input id="country" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.country || 'Nederland'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Telefoonnummer</Label>
                  {editing ? (
                    <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+31 20 123 4567" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.phone || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="website" className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Website</Label>
                  {editing ? (
                    <Input id="website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://www.bedrijf.nl" />
                  ) : (
                    <p className="text-sm text-neutral-700">{organization?.website || '-'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label htmlFor="sector" className="text-xs font-medium text-neutral-500 uppercase tracking-wider inline-flex items-center gap-1 cursor-help">
                          Sector <Info className="h-3 w-3 text-neutral-400" />
                        </Label>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px] text-xs">
                        {fieldTooltips.sector}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {editing ? (
                    <Select value={formData.sector} onValueChange={(v) => setFormData({ ...formData, sector: v })}>
                      <SelectTrigger data-testid="org-sector-select">
                        <SelectValue placeholder="Selecteer sector" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zorg">Gezondheidszorg & maatschappelijke dienstverlening</SelectItem>
                        <SelectItem value="financieel">Financi&#235;le dienstverlening</SelectItem>
                        <SelectItem value="ict">Informatie & communicatie</SelectItem>
                        <SelectItem value="landbouw">Landbouw, bosbouw en visserij</SelectItem>
                        <SelectItem value="winning">Winning van delfstoffen</SelectItem>
                        <SelectItem value="industrie">Industrie</SelectItem>
                        <SelectItem value="energie">Energievoorziening</SelectItem>
                        <SelectItem value="water">Waterbeheer & afvalverwerking</SelectItem>
                        <SelectItem value="bouw">Bouwnijverheid</SelectItem>
                        <SelectItem value="handel">Groot- en detailhandel</SelectItem>
                        <SelectItem value="vervoer">Vervoer en opslag</SelectItem>
                        <SelectItem value="horeca">Horeca</SelectItem>
                        <SelectItem value="vastgoed">Onroerend goed</SelectItem>
                        <SelectItem value="zakelijk">Zakelijke dienstverlening</SelectItem>
                        <SelectItem value="overheid">Overheid & publieke sector</SelectItem>
                        <SelectItem value="onderwijs">Onderwijs</SelectItem>
                        <SelectItem value="cultuur">Cultuur, sport & recreatie</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-neutral-700">{
                      {landbouw:'Landbouw, bosbouw en visserij', winning:'Winning van delfstoffen', industrie:'Industrie', energie:'Energievoorziening', water:'Waterbeheer & afvalverwerking', bouw:'Bouwnijverheid', handel:'Groot- en detailhandel', vervoer:'Vervoer en opslag', horeca:'Horeca', ict:'Informatie & communicatie', financieel:'Financi\u00eble dienstverlening', vastgoed:'Onroerend goed', zakelijk:'Zakelijke dienstverlening', overheid:'Overheid & publieke sector', onderwijs:'Onderwijs', zorg:'Gezondheidszorg & maatschappelijke dienstverlening', cultuur:'Cultuur, sport & recreatie'}[organization?.sector] || organization?.sector || '-'
                    }</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Lid sinds</Label>
                  <p className="text-sm text-neutral-700" data-testid="org-lid-sinds">{organization?.created_at ? new Date(organization.created_at).toLocaleDateString('nl-NL') : '-'}</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</Label>
                  <p className="text-sm"><Badge className="bg-green-50 text-green-700 border-green-200" data-testid="org-status">Actief</Badge></p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

          {/* Team */}

          {/* Team Management Card */}
          <Card className="lg:col-span-2 border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]" data-testid="team-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" strokeWidth={1.5} />
                  Team
                  <Badge className="bg-neutral-100 text-neutral-600 ml-1">{teamMembers.length}</Badge>
                </CardTitle>
                <CardDescription className="text-xs mt-1">Beheer teamleden en hun rollen</CardDescription>
              </div>
              <Button
                size="sm"
                className="bg-black text-white hover:bg-neutral-800"
                onClick={() => setShowInviteDialog(true)}
                data-testid="invite-member-button"
              >
                <UserPlus className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                Uitnodigen
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingTeam ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
                </div>
              ) : (
                <>
                  {/* Members List */}
                  <div className="space-y-2" data-testid="team-members-list">
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${selectedMember?.id === member.id ? 'bg-neutral-100 border-black' : 'bg-neutral-50 border-neutral-100 hover:bg-neutral-100'}`}
                        onClick={() => setSelectedMember(member)}
                        data-testid={`team-member-${member.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-semibold text-neutral-700">
                            {member.full_name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-black">{member.full_name || member.email}</p>
                            <p className="text-[11px] text-neutral-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {member.id === user?.id && (
                            <Badge className="bg-blue-50 text-blue-700 text-[10px]">Jij</Badge>
                          )}
                          <Badge className={`text-[10px] ${member.role === 'admin' ? 'bg-amber-50 text-amber-700' : 'bg-neutral-100 text-neutral-600'}`}>
                            {member.role === 'admin' ? 'Admin' : 'Gebruiker'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pending Invites */}
                  {pendingInvites.length > 0 && (
                    <div className="space-y-2" data-testid="pending-invites-list">
                      <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Clock className="h-3 w-3" strokeWidth={1.5} />
                        Openstaande uitnodigingen ({pendingInvites.length})
                      </p>
                      {pendingInvites.map((invite) => (
                        <div
                          key={invite.token}
                          className="flex items-center justify-between p-3 rounded-lg bg-amber-50/50 border border-amber-100"
                          data-testid={`pending-invite-${invite.email}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
                              <Mail className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-neutral-700">{invite.email}</p>
                              <p className="text-[10px] text-neutral-400">
                                Verstuurd {new Date(invite.created_at).toLocaleDateString('nl-NL')} · Verloopt {new Date(invite.expires_at).toLocaleDateString('nl-NL')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">In afwachting</Badge>
                            <button
                              onClick={() => handleCancelInvite(invite.token)}
                              className="p-1.5 rounded-md text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              data-testid={`cancel-invite-${invite.email}`}
                            >
                              <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

        </div>
      </div>

      {/* Team Member RightPanel */}
      <RightPanel
        open={!!selectedMember}
        onClose={() => setSelectedMember(null)}
        title={selectedMember?.full_name || selectedMember?.email || 'Teamlid'}
        subtitle={selectedMember?.email}
        testId="team-member-panel"
        actions={selectedMember && selectedMember.id !== user?.id ? (
          <>
            <Button
              variant="outline"
              size="sm"
              className="text-xs text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => { setConfirmRemove(selectedMember); setSelectedMember(null); }}
              data-testid="panel-remove-member"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
              Verwijderen
            </Button>
            <div className="flex-1" />
          </>
        ) : null}
      >
        {selectedMember && (
          <div className="space-y-6">
            {/* Avatar + Name */}
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-neutral-200 flex items-center justify-center text-xl font-bold text-neutral-700">
                {selectedMember.full_name?.charAt(0)?.toUpperCase() || selectedMember.email?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="text-sm font-semibold text-black">{selectedMember.full_name || 'Onbekend'}</p>
                <p className="text-xs text-neutral-500">{selectedMember.email}</p>
                {selectedMember.id === user?.id && (
                  <Badge className="bg-blue-50 text-blue-700 text-[10px] mt-1">Dit ben jij</Badge>
                )}
              </div>
            </div>

            {/* Role */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Rol</p>
              {selectedMember.id === user?.id ? (
                <Badge className={`text-xs ${selectedMember.role === 'admin' ? 'bg-amber-50 text-amber-700' : 'bg-neutral-100 text-neutral-600'}`}>
                  {selectedMember.role === 'admin' ? 'Admin' : 'Gebruiker'}
                </Badge>
              ) : (
                <Select
                  value={selectedMember.role || 'user'}
                  onValueChange={(val) => { handleChangeRole(selectedMember.id, val); setSelectedMember({ ...selectedMember, role: val }); }}
                >
                  <SelectTrigger className="h-8 w-full text-xs border-neutral-200" data-testid="panel-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Gebruiker: kan data bekijken en bewerken</SelectItem>
                    <SelectItem value="admin">Admin: kan teamleden en instellingen beheren</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Info */}
            <div className="space-y-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Details</p>
              <div className="flex items-center justify-between py-2 border-b border-neutral-100">
                <span className="text-xs text-neutral-500">Status</span>
                <Badge className="bg-green-50 text-green-700 text-[10px]">Actief</Badge>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-neutral-100">
                <span className="text-xs text-neutral-500">Rol</span>
                <span className="text-xs font-semibold text-black">{selectedMember.role === 'admin' ? 'Admin' : 'Gebruiker'}</span>
              </div>
              {selectedMember.last_login && (
                <div className="flex items-center justify-between py-2 border-b border-neutral-100">
                  <span className="text-xs text-neutral-500">Laatst ingelogd</span>
                  <span className="text-xs font-semibold text-black">{new Date(selectedMember.last_login).toLocaleDateString('nl-NL')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </RightPanel>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Teamlid uitnodigen</DialogTitle>
            <DialogDescription>
              Stuur een uitnodiging per e-mail om een nieuw lid toe te voegen aan uw organisatie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-3">
              <Label htmlFor="invite-email" className="text-sm text-neutral-600">E-mailadres</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="collega@bedrijf.nl"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-3">
              <Label className="text-sm text-neutral-600">Rol</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Gebruiker: kan data bekijken en bewerken</SelectItem>
                  <SelectItem value="admin">Admin: kan ook teamleden en instellingen beheren</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
              <p className="text-xs text-neutral-500">
                De uitnodiging is 7 dagen geldig. De ontvanger maakt een account aan en wordt automatisch toegevoegd aan uw organisatie.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Annuleren</Button>
            <Button
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="send-invite-button"
            >
              {inviting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
              Uitnodiging versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={() => setConfirmRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lid verwijderen</DialogTitle>
            <DialogDescription>
              Weet u zeker dat u <strong>{confirmRemove?.full_name || confirmRemove?.email}</strong> wilt verwijderen uit de organisatie?
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-xs text-red-700">
                Dit lid verliest direct toegang tot alle organisatiegegevens. Deze actie kan niet ongedaan worden gemaakt.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>Annuleren</Button>
            <Button
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleRemoveMember}
              disabled={removing}
              data-testid="confirm-remove-member"
            >
              {removing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default OrganizationPage;
